import React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

export function LifeSquares({ currentAge, targetAge = 100, name }) {
  // Use the maximum between targetAge and 100 for the grid
  const maxYears = Math.max(targetAge, 100);
  const years = Array.from({ length: maxYears }, (_, i) => i + 1);
  const yearsRemaining = Math.max(0, targetAge - currentAge);

  const getSquareClass = (year) => {
    if (year < currentAge) {
      return 'life-square lived';
    } else if (year === currentAge) {
      return 'life-square current';
    } else if (year <= targetAge) {
      return 'life-square future';
    } else {
      return 'life-square beyond';
    }
  };

  return (
    <TooltipProvider delayDuration={100}>
      <div className="space-y-6">
        <div className="text-center space-y-2">
          <h3 className="text-xl font-semibold text-foreground">
            Visualización para: <span className="text-primary">{name}</span>
          </h3>
          <p className="text-sm text-muted-foreground">
            Edad Actual: <strong className="text-primary">{currentAge}</strong> años | 
            Edad Objetivo: <strong className="text-primary">{targetAge}</strong> años
          </p>
        </div>

        <div className="bg-amber-100 dark:bg-amber-900/30 border-l-4 border-amber-400 p-4 rounded-r-lg">
          <p className="text-sm text-amber-800 dark:text-amber-200">
            Recuerda que biológicamente solo un pequeño porcentaje de la población supera los 95 años. 
            Esta herramienta es para visualizar el tiempo y fomentar la reflexión, no una predicción. 
            La calidad de vida es tan importante como la longevidad.
          </p>
        </div>

        <div className="text-center">
          <h4 className="text-lg font-medium mb-4">Tu Línea de Vida Visualizada</h4>
        </div>

        <div 
          className="life-squares-grid max-w-md mx-auto p-4 bg-card rounded-xl shadow-sm border"
          data-testid="life-squares-grid"
        >
          {years.map((year) => (
            <Tooltip key={year}>
              <TooltipTrigger asChild>
                <div
                  className={getSquareClass(year)}
                  data-testid="life-squares-year-cell"
                >
                  {year % 10 === 0 ? year : ''}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Año {year}</p>
              </TooltipContent>
            </Tooltip>
          ))}
        </div>

        <div className="flex flex-wrap justify-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded bg-pink-400"></div>
            <span>Años vividos</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded bg-red-500"></div>
            <span>Año actual</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded bg-blue-400 dark:bg-blue-500"></div>
            <span>Años por vivir</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded bg-gray-300 dark:bg-gray-600"></div>
            <span>Más allá del objetivo</span>
          </div>
        </div>

        <div className="text-center space-y-3 pt-4">
          <h3 className="text-2xl font-bold">
            Te quedan <span className="text-primary">{yearsRemaining}</span> cuadraditos de vida 
            hasta los <span className="text-primary">{targetAge}</span> años.
          </h3>
          <p className="text-lg font-medium text-muted-foreground">
            ¿CÓMO APROVECHARÁS LOS {yearsRemaining} CUADRADITOS QUE TE QUEDAN?
          </p>
        </div>
      </div>
    </TooltipProvider>
  );
}

export default LifeSquares;
